<!--@format-->
<template>
    <div class="mdf-add-budget-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ title }}</h3>
                <div class="widget-header-actions">
                    <div class="svg-container widget-close-button" @click="closeWidget">
                        <widget-close-svg />
                    </div>
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="loading" class="loading-card">
                    <loading-svg />
                </div>
                <div v-else class="form-row">
                    <app-input
                        v-model="budget.title"
                        label="Title"
                        placeholder="Give budget a name"
                    />
                    <app-input
                        v-model="budget.amount"
                        type="number"
                        step="0.01"
                        label="Budget"
                        placeholder="Enter budget"
                        prefix="$"
                        min="1"
                    />
                    <div class="dropdown-container">
                        <label>Fund Type</label>
                        <app-select-dropdown v-model="budget.type" :items="typeItems" />
                    </div>
                    <div>
                        <label
                            >Do you want to make this budget visible to your partners so they may
                            request funds? (The budget amount will not be shown)</label
                        >
                        <app-switch v-model="budget.visibility" size="large" switch-text="YES" />
                    </div>
                    <app-date-picker
                        v-model="budget.availability.date"
                        label="Budget Availability Date"
                        placeholder="Enter budget availability date"
                    />
                    <app-date-picker
                        v-model="budget.expiration.date"
                        description="If no date is entered budget won’t expire"
                        :is-valid="!expirationDateError"
                        :error="expirationDateError"
                        label="Budget Expiration Date"
                        placeholder="Enter budget expiration date"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="close-button" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :disabled="!isValid" :loading="loading" @click="handleSave">
                        Save
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppInput = () => import('@/components/common/app-input');
const AppSelectDropdown = () => import('@/components/common/app-select-dropdown');
const AppSwitch = () => import('@/components/common/app-switch');
const AppDatePicker = () => import('@/components/common/app-date-picker');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { MDFBudgetBoardServiceMixin } from '@/mixins/board/mdf-budget-board-service-mixin';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'mdf-add-budget-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-select-dropdown': AppSelectDropdown,
        'app-date-picker': AppDatePicker,
        'app-switch': AppSwitch,
        'widget-close-svg': WidgetCloseSvg,
        'loading-svg': LoadingSvg,
    },

    mixins: [MDFBudgetBoardServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            budget: {
                title: null,
                amount: null,
                type: null,
                visibility: true,
                availability: {
                    date: null,
                },

                expiration: {
                    date: null,
                },
            },

            editId: null,

            typeItems: [
                { name: 'MDF', id: 'mdf' },
                { name: 'Co-Op', id: 'co-op' },
                { name: 'Rebate', id: 'rebate' },
                { name: 'SPIFF', id: 'spiff' },
            ],
        };
    },

    computed: {
        budgetToEdit() {
            const { options } = this.widget;
            return options.budget;
        },

        updateEmitter() {
            const { options } = this.widget;
            return options.emitter;
        },

        title() {
            const { options } = this.widget;
            return options.title;
        },

        todayDate() {
            return this.$moment().utc().format('YYYY-MM-DD');
        },

        availabilityDate() {
            return this.$moment(this.budget.availability.date).utc().format('YYYY-MM-DD');
        },

        expirationDate() {
            return this.$moment(this.budget.expiration.date).utc().format('YYYY-MM-DD');
        },

        expirationDateError() {
            return this.availabilityDate > this.expirationDate
                ? 'Your expiration date must be after your availability date'
                : null;
        },

        isValid() {
            const { title, amount, type, availability } = this.budget;
            return (
                title &&
                amount &&
                type &&
                type.name &&
                availability &&
                availability.date &&
                !this.expirationDateError &&
                this.expirationDate &&
                this.availabilityDate
            );
        },

        isMasterAgencyUser() {
            return this.$store.getters.isMasterAgencyUser;
        },

        isOrganizationUser() {
            return this.$store.getters.isOrganizationUser;
        },

        isFundsManager() {
            return (
                this.isMasterAgencyUser ||
                this.isOrganizationUser ||
                this.$store.state.user.roles.includes('FundsManager')
            );
        },
    },

    created() {
        this.validateRoles();

        if (this.budgetToEdit) {
            const { title, budget, visibility, type, availability, expiration, id } =
                this.budgetToEdit;

            this.editId = id;
            this.budget.title = title;
            this.budget.amount = budget.amount;
            this.budget.type = this.typeItems.find((option) => option.name === type);
            this.budget.visibility = visibility.value;
            this.budget.availability.date = availability.toDate();
            this.budget.expiration.date = expiration ? expiration.toDate() : null;
        }
    },

    methods: {
        validateRoles() {
            if (!this.isFundsManager) {
                this.closeWidget();
                const errorMessage =
                    'You need additional permissions in order to perform this action.';
                this.$helpers.showFailNotification(errorMessage, { level: LogLevel.INFO });
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleSave() {
            if (this.editId != null) {
                this.loading = true;
                const payload = {
                    budget: this.budget,
                    id: this.editId,
                };
                EventBus.$emit('mdf-add-budget:update', payload);
                this.closeWidget();
            } else {
                this.loading = true;
                this.addNewBudget({ budget: this.budget })
                    .then(() => {
                        this.$helpers.showSuccessNotification('MDF budget was successfully added.');
                        if (this.updateEmitter) {
                            EventBus.$emit(this.updateEmitter);
                        }
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while adding your MDF budget.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.closeWidget();
                        this.loading = false;
                    });
            }
        },
    },
};
</script>

<style>
.mdf-add-budget-widget.widget .content-wrapper {
    padding: 24px;
}

.mdf-add-budget-widget .content-wrapper .form-row {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
}

.mdf-add-budget-widget .content-wrapper .form-row .app-input .input-container {
    display: flex;
    align-items: center;
    column-gap: 8px;
}

.mdf-add-budget-widget .content-wrapper .form-row .app-input .prefix {
    position: unset;
    font-weight: 500;
}

.mdf-add-budget-widget .content-wrapper .form-row .app-input.has-prefix input {
    padding-left: 16px;
}

.mdf-add-budget-widget .content-wrapper .form-row label {
    font-weight: 500;
}

.mdf-add-budget-widget .dropdown-container .app-input {
    height: 32px;
    width: 60%;
}

.mdf-add-budget-widget .dropdown-container .v-select__selection {
    font: normal normal normal 14px/20px Roboto;
}

.mdf-add-budget-widget .dropdown-container .v-list {
    padding: 0;
}

.mdf-add-budget-widget .dropdown-container .v-list .v-list-item__title {
    font-size: 12px;
}

.mdf-add-budget-widget .dropdown-container .v-list-item--active .v-list-item__title {
    color: #18aeff;
}

.mdf-add-budget-widget .dropdown-container .v-input__slot {
    margin: 0;
    padding: 0;
}

.mdf-add-budget-widget .dropdown-container .v-text-field__details {
    display: none;
}

.mdf-add-budget-widget .app-date-picker .vdp-datepicker__calendar {
    bottom: 40px;
}

.mdf-add-budget-widget .app-date-picker .icon-container .calendar-svg:hover {
    cursor: pointer;
}

.mdf-add-budget-widget .app-date-picker .icon-container .calendar-svg path,
.mdf-add-budget-widget .app-date-picker .icon-container .calendar-svg rect,
.mdf-add-budget-widget .app-date-picker .input-container:hover .icon-container .calendar-svg path,
.mdf-add-budget-widget .app-date-picker .input-container:hover .icon-container .calendar-svg rect {
    fill: #f30070;
    transition: fill 250ms ease-in;
}

.mdf-add-budget-widget .app-date-picker .input-container .icon-container .calendar-svg:hover path,
.mdf-add-budget-widget .app-date-picker .input-container .icon-container .calendar-svg:hover rect,
.mdf-add-budget-widget
    .app-date-picker
    .input-container:focus-within
    .icon-container
    .calendar-svg
    path,
.mdf-add-budget-widget
    .app-date-picker
    .input-container:focus-within
    .icon-container
    .calendar-svg
    rect {
    fill: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.mdf-add-budget-widget .app-button.normal {
    padding: 12px 60px;
}

.mdf-add-budget-widget .app-button.normal.close-button {
    background: transparent;
    color: #382153;
    border: 1px solid #cac6cb;
}

.mdf-add-budget-widget .app-button.normal.close-button:hover {
    background: #e1e0e5;
    color: #382153;
}

.mdf-add-budget-widget .loading-card .loading-screen {
    text-align: center;
    padding: 45px 0;
    margin-left: 0;
}

.mdf-add-budget-widget .app-select-dropdown .app-select.v-input.v-input--is-focused .v-input__slot {
    padding: 0 10px 0 14px;
}
</style>
